import { finishPracticeSession } from '@actions/practiceSession';
import Button from '@base/Button/Button';
import P2 from '@base/P2/P2';
import { PRACTICE_STARTED } from '@constants/segment';
import { PRACTICE_FLOW_TYPE, PRACTICE_TYPE } from '@cont/Practice/constants';
import usePracticeSegmentAnalytics from '@cont/Practice/usePracticeSegmentAnalytics';
import styled from '@emotion/styled';
import useFinishSummary from '@stores/auto-dpp/dppFinishSummaryStore';
import usePracticeStore from '@stores/planner/practiceStore';
import { Spacings, Fonts } from '@styles/index';
import isEmptyObject from '@utils/isEmptyObject';
import pushRoute from '@utils/pushRoute';
import { useDispatch } from 'react-redux';

const AutoDppWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: ${Spacings.SPACING_14B};
  border-top: solid 1px;
  border-top-color: var(--color-divider);
  padding-top: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_4B};
`;

const StyledButton = styled(Button)`
  border-radius: ${Spacings.SPACING_1B};
  font-size: ${Fonts.SMALL};
  height: 40px;
`;

const DppTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const PracticeInfo = styled.div`
  margin-top: ${Spacings.SPACING_1B};
  color: var(--color-text-secondary);
`;

const Title = styled(P2)`
  font-weight: ${Fonts.SEMIBOLD};
  line-height: 18px;
`;

const COMPLETED_STATUS = 1;
const IN_COMPLETE_STATUS = 0;

const AutoDpp = ({ lesson }) => {
  const { goalUid } = lesson;
  const { topicGroupUid, goalSlug } = lesson?.practiceDpp;
  const { firstName, lastName } = lesson.author;
  const { practiceDpp = {} } = lesson?.liveClassDetails;
  const segmentAnalytics = usePracticeSegmentAnalytics(goalUid);
  const { toggleShowSummary, setFinishSummary } = useFinishSummary();
  const { finishPlannerPracticeSession } = usePracticeStore();
  const dispatch = useDispatch();
  if (isEmptyObject(practiceDpp)) {
    return null;
  }
  const {
    status,
    sessionUid,
    solvedQuestions = 0,
    numQuestions = 0,
    uid: quizUID
  } = practiceDpp;

  const isCompleted = status === COMPLETED_STATUS;
  const isInComplete = status === IN_COMPLETE_STATUS;

  const handleButtonClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (isCompleted && sessionUid) {
      finishPlannerPracticeSession({}, false);
      await dispatch(finishPracticeSession(sessionUid));
      setFinishSummary({
        goalSlug,
        goalUID: goalUid,
        sessionId: sessionUid,
        topicGroup: topicGroupUid,
        topicUID: topicGroupUid,
        quizUID,
        type: PRACTICE_FLOW_TYPE.AUTO_DPP,
        educatorName: `${firstName} ${lastName}`
      });
      return toggleShowSummary();
    }
    if (quizUID && topicGroupUid && goalUid && goalSlug) {
      return pushRoute({
        pathname: '/goal/[goalSlug]/[goalUID]/practice/[topicUID]',
        query: {
          goalSlug,
          goalUID: goalUid,
          topic_type: PRACTICE_TYPE.EACH_TOPIC,
          type: PRACTICE_FLOW_TYPE.AUTO_DPP,
          topic_group: topicGroupUid,
          topicUID: topicGroupUid,
          quiz_uid: quizUID,
          educator_name: `${firstName} ${lastName}`,
          source: 'Course'
        }
      }).then(() =>
        segmentAnalytics(PRACTICE_STARTED, {
          topic_uid: null,
          practice_questions: numQuestions,
          topology_id: topicGroupUid,
          topology_name: 'NA',
          topology_level: 'NA',
          last_primary_source: 'Course',
          language: 'NA',
          practice_type: 'Educator Led Practice',
          is_completed: isCompleted ? 'Yes' : 'No'
        })
      );
    }
  };
  const getInfoString = () => {
    if (isCompleted) {
      return 'Completed';
    }
    if (isInComplete && sessionUid && solvedQuestions >= 1) {
      return `${
        numQuestions - solvedQuestions
      } of ${numQuestions} questions remaining`;
    }
    return `${numQuestions} questions`;
  };

  const getBtnVariant = () => {
    if (isCompleted) {
      return 'View practice';
    }
    if (isInComplete && sessionUid && solvedQuestions >= 1) {
      return 'Resume practice';
    }
    return 'Start practice';
  };

  return (
    <>
      <AutoDppWrapper>
        <DppTextWrapper>
          <Title color="var(--color-text-primary)">Practice</Title>
          <PracticeInfo>{getInfoString()}</PracticeInfo>
        </DppTextWrapper>

        <StyledButton
          variant="hollow"
          color="black"
          label={getBtnVariant()}
          width="141px"
          maxHeight="sm"
          onClick={handleButtonClick}
        />
      </AutoDppWrapper>
    </>
  );
};

export default AutoDpp;
